/**
 * DateTimePicker Component
 *
 * A date and time picker that enforces dd-MM-yyyy HH:mm:ss format.
 * Combines DatePicker with time selection.
 */

import { useState, useEffect, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  isBefore,
  isAfter,
  setHours,
  setMinutes,
  setSeconds,
  getHours,
  getMinutes,
  getSeconds,
} from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight, Clock, X } from 'lucide-react';

import { cn } from '@/utils/cn';
import { DATE_TIME_FORMAT, formatDateTime, parseDateTime, isValidDateTimeString } from '@/utils/date.utils';
import { Button } from '../Button';
import { Input } from '../Input';

// ============================================
// Types
// ============================================

export interface DateTimePickerProps {
  /** Current value (Date object or dd-MM-yyyy HH:mm:ss string) */
  value?: Date | string | null;
  /** Change handler - receives Date object */
  onChange?: (date: Date | null) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Label text */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Error message */
  errorMessage?: string;
  /** Required field */
  required?: boolean;
  /** Minimum selectable date */
  minDate?: Date | string;
  /** Maximum selectable date */
  maxDate?: Date | string;
  /** Clearable */
  clearable?: boolean;
  /** Allow manual input */
  allowManualInput?: boolean;
  /** Additional input class */
  className?: string;
  /** Name attribute for forms */
  name?: string;
  /** ID for label association */
  id?: string;
}

// ============================================
// Helper Functions
// ============================================

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  return parseDateTime(value);
}

// Generate time options
function generateTimeOptions(max: number): string[] {
  return Array.from({ length: max + 1 }, (_, i) => i.toString().padStart(2, '0'));
}

const HOURS = generateTimeOptions(23);
const MINUTES = generateTimeOptions(59);
const SECONDS = generateTimeOptions(59);

// ============================================
// DateTimePicker Component
// ============================================

export function DateTimePicker({
  value,
  onChange,
  placeholder = 'dd-mm-yyyy hh:mm:ss',
  disabled = false,
  error = false,
  label,
  helperText,
  errorMessage,
  required = false,
  minDate,
  maxDate,
  clearable = true,
  allowManualInput = true,
  className,
  name,
  id,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [viewDate, setViewDate] = useState(() => new Date());
  const [tempDate, setTempDate] = useState<Date | null>(null);

  const selectId = id || name;
  const displayError = error || !!errorMessage;
  const selectedDate = toDate(value);
  const min = toDate(minDate);
  const max = toDate(maxDate);

  // Working date for time selection
  const workingDate = tempDate || selectedDate || new Date();

  // Sync input value with selected date
  useEffect(() => {
    if (selectedDate) {
      setInputValue(formatDateTime(selectedDate));
      setViewDate(selectedDate);
    } else {
      setInputValue('');
    }
  }, [selectedDate]);

  // Reset temp date when popover closes
  useEffect(() => {
    if (!open) {
      setTempDate(null);
    }
  }, [open]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(viewDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days: Date[] = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  }, [viewDate]);

  // Check if a date is disabled
  const isDateDisabled = (date: Date): boolean => {
    if (min && isBefore(date, min)) return true;
    if (max && isAfter(date, max)) return true;
    return false;
  };

  // Handle date selection (just updates the date part)
  const handleSelectDate = (date: Date) => {
    if (isDateDisabled(date)) return;

    // Preserve time from working date
    const newDate = setHours(
      setMinutes(
        setSeconds(date, getSeconds(workingDate)),
        getMinutes(workingDate)
      ),
      getHours(workingDate)
    );
    setTempDate(newDate);
  };

  // Handle time change
  const handleTimeChange = (type: 'hours' | 'minutes' | 'seconds', val: string) => {
    const numVal = parseInt(val, 10);
    let newDate = tempDate || selectedDate || new Date();

    switch (type) {
      case 'hours':
        newDate = setHours(newDate, numVal);
        break;
      case 'minutes':
        newDate = setMinutes(newDate, numVal);
        break;
      case 'seconds':
        newDate = setSeconds(newDate, numVal);
        break;
    }

    setTempDate(newDate);
  };

  // Apply selection
  const handleApply = () => {
    if (tempDate) {
      onChange?.(tempDate);
    }
    setOpen(false);
  };

  // Handle manual input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);

    if (val === '') {
      onChange?.(null);
      return;
    }

    if (isValidDateTimeString(val)) {
      const parsed = parseDateTime(val);
      if (parsed && !isDateDisabled(parsed)) {
        onChange?.(parsed);
        setViewDate(parsed);
      }
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    if (!inputValue) return;

    if (isValidDateTimeString(inputValue)) {
      const parsed = parseDateTime(inputValue);
      if (parsed && !isDateDisabled(parsed)) {
        setInputValue(formatDateTime(parsed));
      } else {
        setInputValue(selectedDate ? formatDateTime(selectedDate) : '');
      }
    } else {
      setInputValue(selectedDate ? formatDateTime(selectedDate) : '');
    }
  };

  // Clear date
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
    setInputValue('');
  };

  // Navigate months
  const goToPrevMonth = () => setViewDate(subMonths(viewDate, 1));
  const goToNextMonth = () => setViewDate(addMonths(viewDate, 1));
  const goToNow = () => {
    const now = new Date();
    setViewDate(now);
    setTempDate(now);
  };

  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-foreground dark:text-foreground-dark"
        >
          {label}
          {required && <span className="ml-1 text-danger">*</span>}
        </label>
      )}

      <Popover.Root open={open} onOpenChange={setOpen}>
        <div className="relative">
          <Popover.Trigger asChild disabled={disabled}>
            <div>
              <Input
                id={selectId}
                name={name}
                type="text"
                value={inputValue}
                onChange={allowManualInput ? handleInputChange : undefined}
                onBlur={allowManualInput ? handleInputBlur : undefined}
                placeholder={placeholder}
                disabled={disabled}
                error={displayError ? errorMessage : undefined}
                readOnly={!allowManualInput}
                className={cn('pr-16', className)}
                onClick={() => !allowManualInput && setOpen(true)}
              />
            </div>
          </Popover.Trigger>

          {/* Icons */}
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
            {clearable && selectedDate && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="rounded p-1 hover:bg-secondary-100 dark:hover:bg-secondary-800"
              >
                <X className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(true)}
              disabled={disabled}
              className="rounded p-1 hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:cursor-not-allowed"
            >
              <Calendar className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
            </button>
          </div>
        </div>

        <Popover.Portal>
          <Popover.Content
            className={cn(
              'z-50 w-80 rounded-lg border shadow-lg',
              'border-border dark:border-border-dark',
              'bg-card dark:bg-card-dark',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
            )}
            sideOffset={4}
            align="start"
          >
            {/* Calendar Header */}
            <div className="flex items-center justify-between border-b border-border p-3 dark:border-border-dark">
              <button
                type="button"
                onClick={goToPrevMonth}
                className="rounded p-1.5 hover:bg-secondary-100 dark:hover:bg-secondary-800"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-semibold text-foreground dark:text-foreground-dark">
                {format(viewDate, 'MMMM yyyy')}
              </span>
              <button
                type="button"
                onClick={goToNextMonth}
                className="rounded p-1.5 hover:bg-secondary-100 dark:hover:bg-secondary-800"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="p-3">
              {/* Weekday Headers */}
              <div className="mb-2 grid grid-cols-7 gap-1">
                {WEEKDAYS.map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, i) => {
                  const isCurrentMonth = isSameMonth(day, viewDate);
                  const isSelected = tempDate
                    ? isSameDay(day, tempDate)
                    : selectedDate && isSameDay(day, selectedDate);
                  const isTodayDate = isToday(day);
                  const isDisabled = isDateDisabled(day);

                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleSelectDate(day)}
                      disabled={isDisabled}
                      className={cn(
                        'h-8 w-8 rounded text-sm',
                        'transition-colors',
                        !isCurrentMonth && 'text-foreground-muted/50 dark:text-foreground-dark-muted/50',
                        isCurrentMonth && 'text-foreground dark:text-foreground-dark',
                        !isSelected && !isDisabled && 'hover:bg-secondary-100 dark:hover:bg-secondary-800',
                        isSelected && 'bg-primary text-white',
                        isTodayDate && !isSelected && 'border border-primary',
                        isDisabled && 'cursor-not-allowed opacity-50'
                      )}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time Selection */}
            <div className="border-t border-border px-3 py-2 dark:border-border-dark">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
                <span className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                  Time:
                </span>
                <div className="flex items-center gap-1">
                  <select
                    value={getHours(workingDate).toString().padStart(2, '0')}
                    onChange={(e) => handleTimeChange('hours', e.target.value)}
                    className={cn(
                      'h-8 w-14 rounded border border-border px-1 text-center text-sm',
                      'bg-card dark:bg-card-dark dark:border-border-dark',
                      'text-foreground dark:text-foreground-dark',
                      'focus:outline-none focus:ring-2 focus:ring-primary'
                    )}
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  <span className="text-foreground-muted dark:text-foreground-dark-muted">:</span>
                  <select
                    value={getMinutes(workingDate).toString().padStart(2, '0')}
                    onChange={(e) => handleTimeChange('minutes', e.target.value)}
                    className={cn(
                      'h-8 w-14 rounded border border-border px-1 text-center text-sm',
                      'bg-card dark:bg-card-dark dark:border-border-dark',
                      'text-foreground dark:text-foreground-dark',
                      'focus:outline-none focus:ring-2 focus:ring-primary'
                    )}
                  >
                    {MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <span className="text-foreground-muted dark:text-foreground-dark-muted">:</span>
                  <select
                    value={getSeconds(workingDate).toString().padStart(2, '0')}
                    onChange={(e) => handleTimeChange('seconds', e.target.value)}
                    className={cn(
                      'h-8 w-14 rounded border border-border px-1 text-center text-sm',
                      'bg-card dark:bg-card-dark dark:border-border-dark',
                      'text-foreground dark:text-foreground-dark',
                      'focus:outline-none focus:ring-2 focus:ring-primary'
                    )}
                  >
                    {SECONDS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-border p-2 dark:border-border-dark">
              <Button variant="ghost" size="sm" onClick={goToNow}>
                Now
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-muted dark:text-foreground-dark-muted">
                  {DATE_TIME_FORMAT.toLowerCase()}
                </span>
                <Button variant="primary" size="sm" onClick={handleApply}>
                  Apply
                </Button>
              </div>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {(helperText || errorMessage) && (
        <p
          className={cn(
            'text-xs',
            displayError
              ? 'text-danger'
              : 'text-foreground-muted dark:text-foreground-dark-muted'
          )}
        >
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}
